import React from "react";
import UserPicker from "@/components/SelectUser2";
import TipTapEditor from "@/components/TipTapEditor";

export default ({ values, setValues }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-4 space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-name">
            Name*
          </label>
          <input
            id="budget-name"
            className="input w-full"
            value={values.name}
            onChange={(e) => {
              setValues({ ...values, name: e.target.value });
            }}
            placeholder="Enter budget name"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-status">
            Status
          </label>
          <select id="budget-status" className="input w-full" value={values.status} onChange={(e) => setValues({ ...values, status: e.target.value })}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Deal Status</label>
          {/* Won is automatic (a signed budget = won). The only manual outcome is "lost". */}
          <select className="input w-full" value={values.lost ? "lost" : "open"} onChange={(e) => setValues({ ...values, lost: e.target.value === "lost" })}>
            <option value="open">{values.signed_at ? "Signé (auto)" : "En cours"}</option>
            <option value="lost">Perdu</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Owner</label>
          <UserPicker
            value={values.userId ? [{ user_id: values.userId, user_name: values.userName, user_avatar: values.userAvatar }] : []}
            onChange={(arr) => {
              const owner = arr && arr.length > 0 ? arr[arr.length - 1] : null;
              setValues({ ...values, userId: owner?.user_id, userName: owner?.user_name, userAvatar: owner?.user_avatar });
            }}
            roles={["admin", "user"]}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-visibility">
            Visibility
          </label>
          <select id="budget-visibility" className="input w-full" value={values.visibility} onChange={(e) => setValues({ ...values, visibility: e.target.value })}>
            <option value="public">Public</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-confidence">
            Confidence
          </label>
          <div className="relative">
            <input
              id="budget-confidence"
              className="input w-full pr-6"
              type="number"
              min="0"
              max="100"
              value={values.confidence || ""}
              onChange={(e) => setValues({ ...values, confidence: Math.min(100, Math.max(0, e.target.value)) })}
              placeholder="Enter confidence"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">%</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Budget Type</label>
        <div className="grid grid-cols-5 gap-2">
          {[
            { type: "project", label: "Project", description: "Budget officially approved by the client" },
            { type: "overbudget", label: "Overbudget", description: "Additional budget for expenses exceeding initial project scope - at Selego's costs" },
            { type: "admin", label: "Admin", description: "Budget for administrative and operational expenses - at Selego's costs" },
          ].map(({ type, label, description }) => (
            <div
              key={type}
              className={`p-4 border rounded-lg cursor-pointer transition-all  hover:border-gray-300 ${values.type === type ? "border-blue-500 bg-blue-100" : "border-gray-200"}`}
              onClick={() => setValues({ ...values, type })}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-gray-800">{label}</span>
              </div>
              <p className="text-sm text-gray-700">{description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Sales Type</label>
        <div className="grid grid-cols-5 gap-2">
          {[
            { sales_type: "new", label: "New", description: "First budget signed with this client" },
            { sales_type: "renew", label: "Renew", description: "Continuation of an existing budget, with the same scope and terms extended or repeated" },
            { sales_type: "upsell", label: "Upsell", description: "Additional budget signed for extra work or a new scope, on top of first agreement" },
          ].map(({ sales_type, label, description }) => (
            <div
              key={sales_type}
              className={`p-4 border rounded-lg cursor-pointer transition-all  hover:border-gray-300 ${
                values.sales_type === sales_type ? "border-blue-500 bg-blue-100" : "border-gray-200"
              }`}
              onClick={() => setValues({ ...values, sales_type })}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-gray-800">{label}</span>
              </div>
              <p className="text-sm text-gray-700">{description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-x-2 flex items-center">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-expense">
          Mark this budget as an expense
        </label>
        <input
          type="checkbox"
          id="budget-expense"
          className="rounded text-blue-600 focus:ring-blue-500"
          checked={values.isExpense || false}
          onChange={(e) => setValues({ ...values, isExpense: e.target.checked })}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-description">
          Description
        </label>
        <div className="border border-gray-200 rounded-lg p-2">
          <TipTapEditor
            buttonList={["bold", "italic", "link", "image", "bulletList", "orderedList", "blockquote", "codeBlock"]}
            values={values.description}
            onChange={(e) => setValues({ ...values, description: e })}
            placeholder="Type your message..."
          />
        </div>
      </div>
    </div>
  );
};
